import React, { useEffect, useState } from "react";
import logger from "../../logger";
import {
  Button,
  Text,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowSyncRegular,
  AddRegular,
  TableAddRegular,
  DeleteRegular,
} from "@fluentui/react-icons";
import { fetchAllFunds, fetchSelectedFund } from "./fundService";
import { useFundManagementStyles } from "./FundManagement.styles";

const FundManagement = () => {
  const [excelData, setExcelData] = useState("");
  const [excelError, setExcelError] = useState(false);
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState("");
  const [fundsLoading, setFundsLoading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [fundDataRange, setFundDataRange] = useState(null);
  const styles = useFundManagementStyles();

  const fundNameToIdMap = React.useMemo(() => {
    const map = {};
    funds.forEach((fund) => {
      const fundName = fund.name || fund.id;
      map[fundName] = fund.id;
    });
    return map;
  }, [funds]);

  const selectedFundDisplayName = React.useMemo(() => {
    if (!selectedFundId) return undefined;
    const fund = funds.find((f) => f.id === selectedFundId);
    return fund ? fund.name || fund.id : undefined;
  }, [selectedFundId, funds]);

  useEffect(() => {
    loadFunds();
  }, []);

  const loadFunds = async () => {
    try {
      setFundsLoading(true);
      const fundsData = await fetchAllFunds();
      setFunds(fundsData);
    } catch (error) {
      logger.error("Error loading funds:", error);
      setExcelData("Error loading funds: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setFundsLoading(false);
    }
  };

  const insertFundDataToExcel = async (fundsData) => {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        if (!fundsData || fundsData.length === 0) {
          throw new Error("No fund data to insert");
        }

        const selectedRange = context.workbook.getSelectedRange();
        selectedRange.load("rowIndex, columnIndex");
        await context.sync();

        const startRow = selectedRange.rowIndex + 1;
        const startColIndex = selectedRange.columnIndex;

        const headers = ["Fund Id", "Fund Name", "Custom Id"];

        const result = [headers];

        for (let i = 0; i < fundsData.length; i++) {
          const fund = fundsData[i];
          if (!fund) continue;

          const fundId = fund.id || "";
          const fundName = fund.name || "";
          const customId = fund.customId || "";

          const row = [fundId, fundName, customId];

          result.push(row);
        }

        const numRows = result.length;
        const numCols = headers.length;

        const getColumnLetter = (colNum) => {
          let result = "";
          let num = colNum;
          while (num > 0) {
            num--;
            result = String.fromCharCode(65 + (num % 26)) + result;
            num = Math.floor(num / 26);
          }
          return result;
        };

        const startCol = getColumnLetter(startColIndex + 1);
        const endCol = getColumnLetter(startColIndex + numCols);
        const endRow = startRow + numRows - 1;

        const dataRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);
        dataRange.values = result;

        const headerRange = sheet.getRange(`${startCol}${startRow}:${endCol}${startRow}`);
        headerRange.format.fill.color = "#1aebff";
        headerRange.format.font.color = "black";
        headerRange.format.font.bold = true;

        dataRange.format.autofitColumns();

        await context.sync();

        setFundDataRange({
          startCol,
          endCol,
          startRow,
          endRow,
        });

        setExcelData("Fund data successfully inserted into Excel!");
        setExcelError(false);

        setTimeout(() => {
          setExcelData("");
        }, 3000);
      });
    } catch (error) {
      logger.error("Error inserting fund data to Excel:", error);
      setExcelData("Error inserting fund data to Excel: " + (error.message || "Unknown error"));
      setExcelError(true);
    }
  };

  const clearFundDataRange = async () => {
    try {
      if (!fundDataRange) {
        setExcelData("No fund data range found to clear.");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
        return;
      }

      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        const { startCol, endCol, startRow, endRow } = fundDataRange;
        const fullRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);

        fullRange.clear(Excel.ClearApplyTo.all);

        await context.sync();
      });
    } catch (error) {
      logger.error("Error clearing fund data range:", error);
      throw error;
    }
  };

  const handleClearAll = async () => {
    setSelectedFundId("");
    setDropdownKey((prev) => prev + 1);

    try {
      await clearFundDataRange();
      setFundDataRange(null);
      setExcelData("Fund data cleared successfully!");
      setExcelError(false);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } catch (error) {
      logger.error("Error clearing fund data:", error);
      setExcelData("Error clearing fund data: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    }
  };

  const handleSelectedFundGet = async () => {
    if (!selectedFundId) {
      setExcelData("Please select a fund first");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    try {
      setFundsLoading(true);
      const fundData = await fetchSelectedFund(selectedFundId);
      if (fundData && typeof fundData === "object" && !Array.isArray(fundData)) {
        await insertFundDataToExcel([fundData]);
      } else {
        setExcelData("No fund data found");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
      }
    } catch (error) {
      setExcelData("Error fetching selected fund: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setFundsLoading(false);
    }
  };

  const handleInsertAllFunds = async () => {
    if (funds.length === 0) {
      setExcelData("No funds available. Please fetch funds first.");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    await insertFundDataToExcel(funds);
  };

  return (
    <div className={styles.container}>
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          Select a Fund:
        </Text>
        <Dropdown
          key={dropdownKey}
          placeholder={fundsLoading ? "Loading funds..." : "Select a fund"}
          value={selectedFundDisplayName || ""}
          disabled={fundsLoading}
          onOptionSelect={(event, data) => {
            const selectedFundName = data.optionValue || "";
            if (selectedFundName === "__CLEAR__") {
              setSelectedFundId("");
              return;
            }
            const fundId = fundNameToIdMap[selectedFundName] || "";
            setSelectedFundId(fundId);
          }}
          className={styles.dropdown}
        >
          {selectedFundId && (
            <Option key="__CLEAR__" value="__CLEAR__">
              Clear Selection Fund
            </Option>
          )}
          {funds.map((fund) => {
            const fundName = fund.name || fund.id;
            return (
              <Option key={fund.id} value={fundName}>
                {fundName}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      <div className={styles.buttonsContainer}>
        <Tooltip
          withArrow
          content={{ children: "Refetch Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={loadFunds}
            disabled={fundsLoading}
            className={styles.refetchButton}
            icon={<ArrowSyncRegular />}
          />
        </Tooltip>
        <Tooltip
          withArrow
          content={{ children: "Insert Fund", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="primary"
            onClick={handleSelectedFundGet}
            disabled={fundsLoading || !selectedFundId}
            className={styles.insertButton}
            icon={<AddRegular />}
          />
        </Tooltip>
        {funds.length > 0 && (
          <Tooltip
            withArrow
            content={{ children: "Insert All Fund", className: styles.tooltip }}
            relationship="label"
            positioning="below"
          >
            <Button
              appearance="primary"
              onClick={handleInsertAllFunds}
              disabled={fundsLoading}
              className={styles.insertAllButton}
              icon={<TableAddRegular />}
            />
          </Tooltip>
        )}
        <Tooltip
          withArrow
          content={{ children: "Clear Fund Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={handleClearAll}
            disabled={fundsLoading}
            className={styles.clearButton}
            icon={<DeleteRegular />}
          />
        </Tooltip>
      </div>

      {excelData && (
        <MessageBar
          intent={excelError ? "error" : "success"}
          className={excelError ? styles.errorMessage : styles.successMessage}
        >
          <MessageBarBody>{excelData}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

export default FundManagement;
